import { isAuthRefreshFailed } from '@artic/api-client';

// ── Auth refresh kill-switch ─────────────────────────────────────────
// Sentry ARTIC-HUB-12X: when the access token expires, supabase-auth-js
// fires POST /auth/v1/token?grant_type=refresh_token. The library's own
// `_callRefreshToken` debouncer prevents concurrent duplicates, but once
// a refresh fails it clears the deferred — so the *next* getSession()
// caller immediately fires another request. With ~30 direct
// `supabase.auth.getSession()` callsites in the hub (Authorization
// headers in api/*.ts), Realtime channel reconnects calling getSession(),
// and Recoil re-renders re-invoking queries after SIGNED_OUT broadcasts,
// this loops at ~8Hz against Supabase's auth rate limit and traps users.
//
// The api-client's `refreshFailed` latch (custom-fetch.ts) stops
// api-client calls but doesn't reach the supabase-auth-js library's own
// HTTP path. Wrapping the fetch passed via `global.fetch` is the one
// place that catches *every* refresh attempt — auto-refresh ticker,
// _recoverAndRefresh, _callRefreshToken — and lets us short-circuit
// before another request hits Supabase.
//
// Short-circuit conditions:
// 1. `isAuthRefreshFailed()` — api-client has already declared refresh
//    dead; tearing the page down to /login is in flight.
// 2. `location.pathname.startsWith('/login')` — user is sitting on the
//    login page; any in-flight refresh from a previous route is noise.
// 3. `location.pathname.startsWith('/doctor')` — user has been routed to
//    the diagnostic page (typically because they're stuck in a refresh
//    storm). The page's own probes call `/auth/v1/token` via raw fetch
//    (bypassing this guard) so refresh diagnosis still works; we only
//    block refreshes coming from Supabase-client paths (the auto-refresh
//    ticker and any background `getSession()` calls).
//
// Synthetic 401 with a Supabase-shaped JSON body: the library treats
// non-network status codes as non-retryable AuthApiError, calls
// _removeSession() once, fires SIGNED_OUT, and stops. No retry storm.
//
// Narrowly scoped to `/auth/v1/token?grant_type=refresh_token`. We
// deliberately do NOT short-circuit `/auth/v1/user` or other token
// grants (`password`, `pkce`, `id_token`) — those run during sign-in
// and the magic-link callback, which can land on a `/login`-ish URL
// during the brief window before the session-resolved Navigate fires.

export function shouldShortCircuitRefresh(urlString: string): boolean {
  if (!urlString.includes('/auth/v1/token')) return false;
  if (!urlString.includes('grant_type=refresh_token')) return false;
  if (isAuthRefreshFailed()) return true;
  if (typeof window !== 'undefined') {
    const path = window.location.pathname;
    if (path.startsWith('/login') || path.startsWith('/doctor')) return true;
  }
  return false;
}

export function syntheticAuthFailure(): Response {
  return new Response(
    JSON.stringify({
      code: 'refresh_disabled',
      error_code: 'refresh_disabled',
      msg: 'auth refresh disabled while signed-out / on login (ARTIC-HUB-12X)',
    }),
    {
      status: 401,
      headers: { 'content-type': 'application/json' },
    },
  );
}

export const guardedFetch: typeof fetch = (input, init) => {
  const url =
    typeof input === 'string' ? input : input instanceof URL ? input.toString() : input.url;
  if (shouldShortCircuitRefresh(url)) {
    return Promise.resolve(syntheticAuthFailure());
  }
  return globalThis.fetch(input, init);
};
